import { zodResolver } from "@hookform/resolvers/zod"
import { Storefront, Warehouse } from "@phosphor-icons/react"
import { addWeeks, subYears } from "date-fns"
import { useCallback, useState, type JSX } from "react"
import { useForm, useWatch } from "react-hook-form"
import { z } from "zod"

import ForecastManyGraph from "~/components/forecast/ForecastManyGraph"
import { AlertWarning } from "~/components/reusable/Alert"
import { Button, type ButtonSize } from "~/components/reusable/Button"
import { Checkbox } from "~/components/reusable/Checkbox"
import DateRangePicker from "~/components/reusable/DateRangePicker"
import {
  DialogBodyV2,
  DialogClose,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/reusable/Form"
import InfoTooltip from "~/components/reusable/InfoTooltip"
import { Input } from "~/components/reusable/Input"
import Select from "~/components/reusable/Select"
import { trackEvent } from "~/hooks/useTrackingService"
import { type AggregatedAdjustment } from "~/models/adjustment"
import { useUser } from "~/providers/userProvider"
import { useSkuStore } from "~/providers/sku-store-provider"
import { api } from "~/utils/api"
import { useForecastingChannels } from "~/hooks/useChannels"
import { formatNumber } from "~/utils/formatting"
import { useAddAdjustment, useUpdateAdjustment } from "~/hooks/useAdjustments"
import { useFiscalWeek } from "~/hooks/useFiscalWeek"
import {
  DateFormatType,
  endOfDayUTC,
  getEndOfCurrentWeekUTC,
  getStartOfCurrentWeekUTC,
  isAfter,
  setISOWeekDay,
  startOfDayUTC,
  startOfTodayUTC,
  weeksInRange,
} from "~/utils/datetime"

const formSchema = z.object({
  title: z.string().min(1, {
    message: "Required",
  }),
  theme: z.object({
    value: z.string(),
    label: z.string(),
  }),
  valueType: z.object({
    value: z.string(),
    label: z.string(),
  }),
  value: z.string().min(1, {
    message: "Required",
  }),
  locksForecast: z.boolean(),
  warehouse: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .nullable()
    .optional(),
  channel: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .nullable()
    .optional(),
})

type OptionType = { value: string; label: string }
const themeOptions: OptionType[] = [
  { value: "marketing", label: "Marketing" },
  { value: "strategy", label: "Strategy" },
  { value: "misc", label: "Misc" },
]

const valueTypeOptions: OptionType[] = [
  { value: "percentage", label: "Adjust by %" },
  { value: "add", label: "Adjust by units/week" },
  { value: "set", label: "Set weekly demand" },
  { value: "setTotal", label: "Set total demand" },
]

type Props = {
  variantIds: string[]
  buttonSize?: ButtonSize
  adjustment?: AggregatedAdjustment
  customTrigger?: JSX.Element
  customOpen?: boolean
  onClose?: () => void
  presetStartDate?: Date
  presetEndDate?: Date
}

export const ManageAdjustmentDialog = ({
  adjustment,
  customTrigger,
  variantIds,
  buttonSize = "base",
  customOpen,
  onClose,
  presetStartDate,
  presetEndDate,
}: Props) => {
  const { formatWeek } = useFiscalWeek()
  const { permissions } = useUser()
  const storeWarehouseId = useSkuStore((state) => state.warehouseId)
  const storeChannel = useSkuStore((state) => state.channel)

  const [internalOpen, setInternalOpen] = useState(false)
  const open = !!customOpen || internalOpen

  // Adjustments apply per whole ISO week, so date input is week-granular: snap any
  // day-level preset to its week (Monday start / Sunday end). HAK-1678: also floor presets
  // at the current week — a historic range selected on the forecast graph must not seed a
  // historic adjustment, since onSubmit reads this state directly (not the picker value).
  const currentWeekStart = getStartOfCurrentWeekUTC()
  const currentWeekEnd = getEndOfCurrentWeekUTC()
  const presetToWeekStart = (date: Date) => {
    const weekStart = setISOWeekDay(date, 1)
    return isAfter(currentWeekStart, weekStart) ? currentWeekStart : weekStart
  }
  const presetToWeekEnd = (date: Date) => {
    const weekEnd = setISOWeekDay(date, 7)
    return isAfter(currentWeekEnd, weekEnd) ? currentWeekEnd : weekEnd
  }

  const [startDate, setStartDate] = useState(
    (presetStartDate && presetToWeekStart(presetStartDate)) ??
      adjustment?.startDate ??
      addWeeks(currentWeekStart, 1),
  )
  const [endDate, setEndDate] = useState(
    (presetEndDate && presetToWeekEnd(presetEndDate)) ??
      adjustment?.endDate ??
      addWeeks(currentWeekEnd, 2),
  )

  const { data: warehouses } = api.warehouse.get.useQuery(undefined, {
    enabled: variantIds.length > 0 && open,
  })

  const { data: channels } = useForecastingChannels()

  const getDefaultValues = useCallback(() => {
    const presetWarehouse = adjustment
      ? { value: adjustment.warehouseId, label: adjustment.warehouseId }
      : storeWarehouseId && warehouses
        ? warehouses.filter((w: any) => w.isPrimary).find((w: any) => w.id === storeWarehouseId)
          ? {
              value: storeWarehouseId,
              label: warehouses.find((w: any) => w.id === storeWarehouseId)!.name,
            }
          : undefined
        : undefined

    const presetChannel =
      adjustment?.channels.length === 1
        ? { value: adjustment.channels[0]!, label: adjustment.channels[0]! }
        : !adjustment && storeChannel && channels
          ? channels.find((c) => c.id === storeChannel)
            ? { value: storeChannel, label: channels.find((c) => c.id === storeChannel)!.name }
            : undefined
          : undefined

    return {
      title: adjustment?.name,
      theme: themeOptions.find((t) => t.value === adjustment?.theme) ?? themeOptions[0],
      valueType: valueTypeOptions.find((t) => t.value === adjustment?.type) ?? valueTypeOptions[0],
      // Stored values are weight-split per variant/channel/warehouse into single-precision
      // floats; re-summing them drifts (500 → 500.00001). Demand is whole units, so prefill
      // an integer — this also cleans up pre-rounding adjustments stored with fractions.
      value: adjustment ? String(Math.round(adjustment.value)) : undefined,
      warehouse: presetWarehouse,
      channel: presetChannel,
      locksForecast: adjustment?.locksForecast ?? true,
    }
  }, [adjustment, storeWarehouseId, storeChannel, warehouses, channels])

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(),
  })

  const selectedWarehouse = useWatch({ control: form.control, name: "warehouse" })
  const selectedChannel = useWatch({ control: form.control, name: "channel" })
  const value = useWatch({ control: form.control, name: "value" })
  const type = useWatch({ control: form.control, name: "valueType" })

  const targetVariantIds = adjustment?.variantIds ?? variantIds
  const isEditingSubsetOfSelection =
    !!adjustment && targetVariantIds.length > variantIds.length && variantIds.length > 0

  // Set-style types always lock the demand (checkbox forced on + disabled).
  const isSetType = type?.value === "set" || type?.value === "setTotal"
  const isSetTotal = type?.value === "setTotal"
  const setWeeks = isSetType ? weeksInRange(startDate, endDate) : 0
  const setWeeklyUnits = isSetTotal ? Math.round(Number(value) / setWeeks) : Number(value)

  const { data: forecastAvailability } = api.forecasts.haveForecasts.useQuery(
    {
      variantIds: targetVariantIds,
      warehouseId: selectedWarehouse?.value === "Global" ? undefined : selectedWarehouse?.value,
    },
    { enabled: targetVariantIds.length > 0 && open },
  )

  const { data: weeklyPreOrders } = api.preOrder.getPreOrderWeeklyTotalsForVariants.useQuery(
    {
      variantIds: targetVariantIds,
      startDate: startOfDayUTC(startDate),
      endDate: endOfDayUTC(endDate),
      warehouseId: selectedWarehouse?.value === "Global" ? undefined : selectedWarehouse?.value,
      channel: selectedChannel?.value ?? undefined,
    },
    { enabled: targetVariantIds.length > 0 && open && isSetType },
  )

  const weeksWithMorePreorders =
    isSetType && isFinite(setWeeklyUnits)
      ? (weeklyPreOrders ?? []).filter((week) => week.total > setWeeklyUnits)
      : []

  const addAdjustment = useAddAdjustment()
  const updateAdjustment = useUpdateAdjustment()

  const resetForm = () => {
    form.reset(getDefaultValues())
    setStartDate(adjustment?.startDate ?? addWeeks(getStartOfCurrentWeekUTC(), 1))
    setEndDate(adjustment?.endDate ?? addWeeks(getEndOfCurrentWeekUTC(), 2))
  }

  const handleOpenChange = (open: boolean) => {
    setInternalOpen(open)
    if (open) {
      trackEvent("Adjustment Manage Dialog Opened")
      form.reset(getDefaultValues())
      if (presetStartDate) setStartDate(presetToWeekStart(presetStartDate))
      if (presetEndDate) setEndDate(presetToWeekEnd(presetEndDate))
    } else {
      resetForm()
      onClose?.()
    }
  }

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    // Enter-key form submission bypasses the disabled submit button.
    if (!permissions.canEdit) return

    const lockForecast =
      values.valueType.value === "set" || values.valueType.value === "setTotal"
        ? true
        : values.locksForecast
    try {
      if (adjustment) {
        // For updates, we need to handle multiple channels
        // For now, we'll update with the first channel if multiple are selected
        await updateAdjustment.mutateAsync({
          groupId: adjustment.groupId,
          type: values.valueType.value,
          variantIds: targetVariantIds,
          warehouseId: adjustment.warehouseId === "Global" ? undefined : adjustment.warehouseId,
          channel: adjustment.channels.length === 1 ? adjustment.channels[0] : undefined,
          theme: values.theme.value,
          name: values.title,
          value: Number(values.value),
          startDate: startOfDayUTC(startDate),
          endDate: endOfDayUTC(endDate),
          locksForecast: lockForecast,
        })
      } else {
        await addAdjustment.mutateAsync({
          type: values.valueType.value,
          warehouseId: values.warehouse?.value,
          variantIds: variantIds ?? [],
          theme: values.theme.value,
          name: values.title,
          channel: selectedChannel?.value ?? undefined,
          value: Number(values.value),
          startDate: startOfDayUTC(startDate),
          endDate: endOfDayUTC(endDate),
          locksForecast: lockForecast,
        })
      }
      setInternalOpen(false)
      onClose?.()
      resetForm()
    } catch {
      // errors handled by mutation hooks (useAddAdjustment / useUpdateAdjustment)
    }
  }

  const handleStartDateChange = (date: Date | undefined) => {
    if (!date) return
    const weekStart = setISOWeekDay(date, 1)
    setStartDate(weekStart)
    if (isAfter(weekStart, endDate)) {
      setEndDate(setISOWeekDay(addWeeks(weekStart, 1), 7))
    }
  }

  // HAK-1678: adjustments can't reach into the past — floor the picker at the current ISO
  // week (matches getEffectiveForecastStartDate, which clamps forecast start to the current
  // week). When editing an adjustment that already starts earlier, keep its own start
  // reachable so existing historic adjustments stay editable without being moved forward.
  const minAdjustmentDate =
    adjustment && adjustment.startDate.getTime() < currentWeekStart.getTime()
      ? adjustment.startDate
      : currentWeekStart

  const showWarehouse = warehouses && warehouses.length > 1
  const showChannel = channels && channels.length > 1

  return (
    <DialogRoot open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger requires="edit" asChild>
        {customTrigger ? customTrigger : <Button size={buttonSize}>Add adjustment</Button>}
      </DialogTrigger>
      {open && (
        <DialogContentV2 className="max-w-[calc(32rem+20px)]">
          <DialogHeaderV2>
            <DialogTitle>{adjustment ? "Update" : "Add"} adjustment</DialogTitle>
          </DialogHeaderV2>
          <DialogBodyV2>
            {adjustment && (
              <span className="paragraph-xs text-fg-muted">
                Changes apply to all {formatNumber(targetVariantIds.length, 0)} variant
                {targetVariantIds.length !== 1 ? "s" : ""} in this adjustment
                {isEditingSubsetOfSelection &&
                  `, not just the ${formatNumber(variantIds.length, 0)} selected`}
                .
              </span>
            )}
            {forecastAvailability?.hasDemandPlanForecast && (
              <AlertWarning title="Based on a demand plan">
                Adjustments will apply on top of this forecast, but will be overridden if the demand
                plan is published again.
              </AlertWarning>
            )}
            <ForecastManyGraph
              variantIds={targetVariantIds}
              height={220}
              firstDisplayDay={subYears(startOfTodayUTC(), 1)}
              warehouseId={
                selectedWarehouse?.value === "Global" || adjustment?.warehouseId === "Global"
                  ? undefined
                  : selectedWarehouse?.value
                    ? selectedWarehouse.value
                    : adjustment?.warehouseId
                      ? adjustment.warehouseId
                      : undefined
              }
              channels={
                adjustment?.channels ??
                (selectedChannel?.value ? [selectedChannel.value] : undefined)
              }
              adjustment={{
                startDate: startDate,
                endDate: endDate,
                type: type.value,
                value: isFinite(Number(value)) ? Number(value) : 0,
              }}
              oldAdjustment={adjustment}
            />
            <Form {...form}>
              <form
                id="manage-adjustment-form"
                onSubmit={form.handleSubmit(onSubmit)}
                className="grid auto-rows-auto gap-2"
              >
                <div className="grid auto-rows-auto gap-2">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="---" containerClassName="w-full" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    {showWarehouse ? (
                      <FormField
                        control={form.control}
                        name="warehouse"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Warehouse</FormLabel>
                            <FormControl>
                              <Select
                                {...field}
                                options={[
                                  ...warehouses
                                    .filter((w: any) => w.isPrimary)
                                    .map((w: any) => ({
                                      label: w.name,
                                      value: w.id,
                                    })),
                                ]}
                                isClearable
                                placeholder="Global"
                                leftIcon={<Warehouse />}
                                isDisabled={adjustment !== undefined}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ) : null}

                    {showChannel ? (
                      <FormField
                        control={form.control}
                        name="channel"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Channels</FormLabel>
                            <FormControl>
                              <Select
                                {...field}
                                className="w-full"
                                options={channels.map((channel) => ({
                                  label: channel.name,
                                  value: channel.id,
                                }))}
                                isClearable
                                placeholder="All channels"
                                leftIcon={<Storefront />}
                                isDisabled={adjustment !== undefined}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ) : null}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="theme"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Theme</FormLabel>
                          <FormControl>
                            <Select {...field} options={themeOptions} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex flex-col gap-1.5">
                      <label className="label-base text-fg-subtle">Date range</label>
                      <DateRangePicker
                        weekly
                        minDate={minAdjustmentDate}
                        onStartDateChange={handleStartDateChange}
                        onEndDateChange={(date) => {
                          if (date) setEndDate(setISOWeekDay(date, 7))
                        }}
                        startDate={startDate}
                        endDate={endDate}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="valueType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Adjustment Type</FormLabel>
                          <FormControl>
                            <Select
                              isDisabled={!!adjustment}
                              {...field}
                              options={valueTypeOptions}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="value"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Value</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="---"
                              containerClassName="w-full"
                              type="number"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="locksForecast"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex">
                          <FormControl>
                            <Checkbox
                              checked={isSetType || field.value}
                              disabled={isSetType}
                              onCheckedChange={() => field.onChange(!field.value)}
                            />
                          </FormControl>
                          <FormLabel className="flex ml-2 items-center">
                            Locks demand{" "}
                            <InfoTooltip body="A locked demand for the weeks you adjust will not automatically be updated with new demand. Only if you do it manually." />
                          </FormLabel>
                          <FormMessage />
                        </div>
                      </FormItem>
                    )}
                  />

                  {isSetType && (
                    <AlertWarning>
                      {isSetTotal
                        ? `This spreads the total demand evenly across ${setWeeks} week${
                            setWeeks === 1 ? "" : "s"
                          }, overwriting existing adjustments on these variants for the selected dates and locking the demand.`
                        : "This overwrites existing adjustments on these variants for the selected dates and locks the demand."}
                    </AlertWarning>
                  )}

                  {weeksWithMorePreorders.length > 0 && (
                    <AlertWarning>
                      {`Week${weeksWithMorePreorders.length === 1 ? "" : "s"} ${weeksWithMorePreorders
                        .map((week) => formatWeek(week.startOfWeek, DateFormatType.WeekNumber))
                        .join(", ")} ${
                        weeksWithMorePreorders.length === 1 ? "has" : "have"
                      } more preorders than the ${Math.round(setWeeklyUnits)} units per week this sets the demand to.`}
                    </AlertWarning>
                  )}

                  {!forecastAvailability?.hasForecast && (
                    <AlertWarning>
                      You cannot add an adjustment, when there is no forecasts.
                    </AlertWarning>
                  )}
                </div>
              </form>
            </Form>
          </DialogBodyV2>
          <DialogFooterV2 className="justify-between">
            <DialogClose asChild>
              <Button variant="transparent">Cancel</Button>
            </DialogClose>
            <Button
              requires="edit"
              type="submit"
              form="manage-adjustment-form"
              loading={addAdjustment.isPending || updateAdjustment.isPending}
              disabled={!forecastAvailability?.hasForecast}
            >
              {adjustment ? "Update adjustment" : "Add adjustment"}
            </Button>
          </DialogFooterV2>
        </DialogContentV2>
      )}
    </DialogRoot>
  )
}
